import React from 'react';
import moment from 'moment';
import ChartistGraph from 'react-chartist';
import { AccessTime } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import GridItem from 'components/Grid/GridItem.js';
import GridContainer from 'components/Grid/GridContainer.js';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import CardFooter from 'components/Card/CardFooter.js';

import {
  weeklySalesNumbersChart,
  weeklySalesChart,
  yearlySalesChart,
  yearlyExpensesChart
} from 'constants/charts.js';

import styles from 'assets/jss/material-dashboard-react/views/dashboardStyle.js';

const useStyles = makeStyles(styles);

const DashboardCharts = (props) => {
  
  const classes = useStyles();

  const weeklyChartStats = (salesOrNumber) => {

    let series = [[]];
    let labels = [];

    const startDate = moment().subtract(7, 'day');
    const endDate = moment();

    while (startDate.isSameOrBefore(endDate, 'day')) {
      var totalDailySales = 0;
      var totalDailySalesNumber = 0;

      for ( let i = 0; i < props.salesOrders.length; i++ ) {
        const currSalesOrder = props.salesOrders[i];

        // If the First Payment of the Sales Order was made this week, its counted in totalDailySalesNumber.
        if(startDate.isSame(currSalesOrder.startDate, 'day')) totalDailySalesNumber += 1;
        
        // Check the other payments to calculate weekly sales.
        for ( let j = 0; j < currSalesOrder.payments.length; j++ ) {
          if(startDate.isSame(currSalesOrder.payments[j].createdAt, 'day')) {
            totalDailySales += currSalesOrder.payments[j].amount;
          }
        }
      }

      if (salesOrNumber) series[0].push(totalDailySales);
      else series[0].push(totalDailySalesNumber);
      labels.push(startDate.format("ddd"));

      startDate.add(1, 'day');
    }
    return {
      labels,
      series
    };
  };

  const yearlyChartStats = (salesOrExpenses) => {

    let series = [[]];
    let labels = [];

    const startDate = moment().subtract(11, 'month');
    const endDate = moment();

    while (startDate.isSameOrBefore(endDate, 'month')) {

      if(salesOrExpenses) {
        var totalMonthlySales = 0;

        for ( let i = 0; i < props.salesOrders.length; i++ ) {
          const currSalesOrder = props.salesOrders[i];
          for ( let j = 0; j < currSalesOrder.payments.length; j++ ) {
            if(startDate.isSame(currSalesOrder.payments[j].createdAt, 'month')) {
              totalMonthlySales += currSalesOrder.payments[j].amount;
            }
          }
        }
        series[0].push(totalMonthlySales);
      }
      else {
        var totalMonthlyExpenses = 0;

        for ( let i = 0; i < props.expenses.length; i++ ) {
          if(startDate.isSame(props.expenses[i].createdAt, 'month')) {
            totalMonthlyExpenses += props.expenses[i].total;
          }
        }
        series[0].push(totalMonthlyExpenses);
      }

      labels.push(startDate.format("MMM"));
      startDate.add(1, 'month');
    }

    return {
      labels,
      series
    }
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={6}>
        <Card chart>
          <CardHeader color="success">
            <ChartistGraph
              className="ct-chart"
              data={weeklyChartStats(false)}
              type="Line"
              options={weeklySalesNumbersChart.options}
              listener={weeklySalesNumbersChart.animation}
            />
          </CardHeader>
          <CardBody>
            <h4 className={classes.cardTitle}>Weekly Sales Orders</h4>
            <p className={classes.cardCategory}>Number of Sales Orders in the Past Week</p>
          </CardBody>
          <CardFooter chart>
            <div className={classes.stats}>
              <AccessTime /> Current Day - {moment().format("dddd")}
            </div>
          </CardFooter>
        </Card>
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <Card chart>
          <CardHeader color="success">
            <ChartistGraph
              className="ct-chart"
              data={weeklyChartStats(true)}
              type="Bar"
              options={weeklySalesChart.options}
              responsiveOptions={weeklySalesChart.responsiveOptions}
              listener={weeklySalesChart.animation}
            />
          </CardHeader>
          <CardBody>
            <h4 className={classes.cardTitle}>Total Weekly Sales</h4>
            <p className={classes.cardCategory}>Total Sales in the Past Week</p>
          </CardBody>
          <CardFooter chart>
            <div className={classes.stats}>
              <AccessTime /> Current Day - {moment().format("dddd")}
            </div>
          </CardFooter>
        </Card>
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <Card chart>
          <CardHeader color="success">
            <ChartistGraph
              className="ct-chart"
              data={yearlyChartStats(true)}
              type="Line"
              options={yearlySalesChart.options}
              listener={yearlySalesChart.animation}
            />
          </CardHeader>
          <CardBody>
            <h4 className={classes.cardTitle}>Total Monthly Sales</h4>
            <p className={classes.cardCategory}>Growth in Sales since last {moment().add(1, 'month').format("MMMM")}</p>
          </CardBody>
          <CardFooter chart>
            <div className={classes.stats}>
              <AccessTime /> Current Month - {moment().format("MMMM")}
            </div>
          </CardFooter>
        </Card>
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <Card chart>
          <CardHeader color="success">
            <ChartistGraph
              className="ct-chart"
              data={yearlyChartStats(false)}
              type="Bar"
              options={yearlyExpensesChart.options}
              responsiveOptions={yearlyExpensesChart.responsiveOptions}
              listener={yearlyExpensesChart.animation}
            />
          </CardHeader>
          <CardBody>
            <h4 className={classes.cardTitle}>Total Monthly Expenses</h4>
            <p className={classes.cardCategory}>Total Monthly Expenses since last {moment().add(1, 'month').format("MMMM")}</p>
          </CardBody>
          <CardFooter chart>
            <div className={classes.stats}>
              <AccessTime /> Current Month - {moment().format("MMMM")}
            </div>
          </CardFooter>
        </Card>
      </GridItem>
    </GridContainer>
  )
};

export default DashboardCharts;